import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  IconButton,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import LockOpenRoundedIcon from '@mui/icons-material/LockOpenRounded';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import { Link as RouterLink, Navigate, useLocation } from 'react-router-dom';
import { getApiUrl } from '../utils/api';
import { useAuth } from '../auth/AuthContext';

const API_URL = getApiUrl();
const PAYMENT_PENDING_MESSAGE = 'Seu acesso está pendente de pagamento';

const Login = () => {
  const auth = useAuth();
  const location = useLocation();
  const [login, setLogin] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(() => (
    new URLSearchParams(location.search).get('reason') === 'payment_pending'
      ? PAYMENT_PENDING_MESSAGE
      : ''
  ));
  const [showPassword, setShowPassword] = useState(false);
  const loginReason = new URLSearchParams(location.search).get('reason');
  const successMessage = loginReason === 'password_reset'
    ? 'Senha redefinida com sucesso. Entre usando a nova senha.'
    : location.state?.message || '';

  if (auth.isAuthenticated) {
    return <Navigate to="/pedidos" replace />;
  }

  const from = location.state?.from?.pathname || '/pedidos';

  const handleSubmit = async (event) => {
    event.preventDefault();
    setSubmitting(true);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ login, password }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Falha ao entrar no sistema.');
      }

      auth.login({ token: data.token, user: data.user });
      window.location.replace(data.user?.mustChangePassword ? '/trocar-senha' : from);
    } catch (err) {
      setError(err.message || 'Falha ao entrar no sistema.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'grid',
        placeItems: 'center',
        px: 2,
        background: 'radial-gradient(circle at top, #f59e0b 0%, #f97316 26%, #111827 100%)',
      }}
    >
      <Card
        sx={{
          width: '100%',
          maxWidth: 460,
          borderRadius: 6,
          boxShadow: '0 32px 80px rgba(15, 23, 42, 0.28)',
        }}
      >
        <CardContent sx={{ p: 4 }}>
          <Stack spacing={3}>
            <Stack spacing={1}>
              <Box
                sx={{
                  width: 56,
                  height: 56,
                  borderRadius: 3,
                  bgcolor: '#111827',
                  color: '#fff',
                  display: 'grid',
                  placeItems: 'center',
                }}
              >
                <LockOpenRoundedIcon />
              </Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#111827' }}>
                Entrar no sistema
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Acesse com seu login e senha para liberar os módulos conforme seu perfil.
              </Typography>
            </Stack>

            <Box component="form" onSubmit={handleSubmit}>
              <Stack spacing={2}>
                {successMessage ? <Alert severity="success">{successMessage}</Alert> : null}
                {error ? <Alert severity="error">{error}</Alert> : null}
                <TextField
                  label="Login"
                  value={login}
                  onChange={(event) => setLogin(event.target.value)}
                  autoComplete="username"
                  fullWidth
                  required
                />
                <TextField
                  label="Senha"
                  value={password}
                  onChange={(event) => setPassword(event.target.value)}
                  autoComplete="current-password"
                  type={showPassword ? 'text' : 'password'}
                  fullWidth
                  required
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton onClick={() => setShowPassword((prev) => !prev)} edge="end">
                          {showPassword ? <VisibilityOffOutlinedIcon /> : <VisibilityOutlinedIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
                <Button type="submit" size="large" variant="contained" disabled={submitting}>
                  {submitting ? 'Entrando...' : 'Entrar'}
                </Button>
                <Button component={RouterLink} to="/recuperar-senha" variant="text" size="small">
                  Esqueci minha senha
                </Button>
              </Stack>
            </Box>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default Login;
